import React from 'react';
import { Box, Card, Typography, Grid, Fade } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { modernTheme, formatHours, Sparkline } from './theme';

const StatCard = ({ icon, value, label, gradient, color, sparkData, delay = 0 }) => (
  <Fade in timeout={600 + delay}>
    <Card sx={{
      p: { xs: 2, md: 2.5 },
      borderRadius: 3,
      border: `1px solid ${modernTheme.colors.gray[200]}`,
      boxShadow: modernTheme.shadows.sm,
      transition: 'all 0.25s ease',
      cursor: 'default',
      '&:hover': {
        boxShadow: modernTheme.shadows.md,
        borderColor: color,
      }
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px',
            background: gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mb: 1.5,
            boxShadow: modernTheme.shadows.sm,
          }}>
            {icon}
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: modernTheme.colors.gray[900], lineHeight: 1.2 }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{
            color: modernTheme.colors.gray[500],
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: '0.65rem',
          }}>
            {label}
          </Typography>
        </Box>
        {sparkData && sparkData.length >= 2 && (
          <Box sx={{ mt: 'auto', pt: 2, opacity: 0.8 }}>
            <Sparkline data={sparkData} width={64} height={28} color={color} />
          </Box>
        )}
      </Box>
    </Card>
  </Fade>
);

const fmtCompact = (v) => {
  if (!v) return '0đ';
  if (v >= 1000000) return `${(v / 1000000).toFixed(v % 1000000 === 0 ? 0 : 1)}M`;
  if (v >= 1000) return `${Math.round(v / 1000)}K`;
  return new Intl.NumberFormat('vi-VN').format(v) + 'đ';
};

const StatsGrid = ({ monthlySummary, weeklySummary, userSummary, commissionData, baseSalary = 0 }) => {
  const dailyHours = (monthlySummary?.records || [])
    .slice()
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(-7)
    .map(r => {
      if (r.minutes !== undefined) return (r.hours || 0) + (r.minutes || 0) / 60;
      return r.hours || 0;
    });

  const liveEarnings = userSummary?.currentPeriod?.earnings || 0;
  const commission = commissionData?.commissionAmount || 0;
  const totalIncome = baseSalary + liveEarnings + commission;

  const stats = [
    {
      icon: <AccessTimeIcon sx={{ color: 'white', fontSize: 20 }} />,
      value: formatHours(monthlySummary?.totalHours),
      label: 'Giờ tháng này',
      gradient: modernTheme.gradients.primary,
      color: modernTheme.colors.primary,
      sparkData: dailyHours,
    },
    {
      icon: <TimelapseIcon sx={{ color: 'white', fontSize: 20 }} />,
      value: formatHours(weeklySummary?.totalHours),
      label: 'Giờ tuần này',
      gradient: modernTheme.gradients.secondary,
      color: modernTheme.colors.secondary,
      sparkData: dailyHours.slice(-5),
    },
    {
      icon: <AttachMoneyIcon sx={{ color: 'white', fontSize: 20 }} />,
      value: fmtCompact(totalIncome),
      label: 'Thu nhập kỳ này',
      gradient: modernTheme.gradients.accent,
      color: modernTheme.colors.accent,
      sparkData: null,
    },
    {
      icon: <TrendingUpIcon sx={{ color: 'white', fontSize: 20 }} />,
      value: `${formatHours(userSummary?.kpi?.progress, 0)}%`,
      label: 'KPI Progress',
      gradient: `linear-gradient(135deg, ${modernTheme.colors.warning} 0%, #fb923c 100%)`,
      color: modernTheme.colors.warning,
      sparkData: null,
    },
  ];

  return (
    <Grid container spacing={{ xs: 1.5, md: 2.5 }} sx={{ mb: 3 }}>
      {stats.map((stat, i) => (
        <Grid item xs={6} sm={3} key={i}>
          <StatCard {...stat} delay={i * 80} />
        </Grid>
      ))}
    </Grid>
  );
};

export default StatsGrid;
